import { useEffect, useState, type ReactElement } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Alert, Button, Card, EmptyState, Icon, InfoTooltip } from "../../ui";
import { useBreakpoint } from "../../lib/useBreakpoint";
import { ListSkeleton } from "./Skeletons";
import { SandboxCard } from "./SandboxCard";
import { useQuota, useSandboxesStore } from "./store";
import styles from "./SandboxesView.module.css";

const ABOUT =
  "A sandbox is a safe, isolated environment for testing API integrations without affecting live data. Use it to build new integrations or expand existing ones with additional endpoints or use cases before going live.";

export function SandboxesView(): ReactElement {
  const navigate = useNavigate();
  const sandboxes = useSandboxesStore((s) => s.sandboxes);
  const status = useSandboxesStore((s) => s.status);
  const refreshing = useSandboxesStore((s) => s.refreshing);
  const load = useSandboxesStore((s) => s.load);
  const { total, active, maxActive, atQuota, canCreate } = useQuota();
  const { isMobile } = useBreakpoint();

  const [limitsOpen, setLimitsOpen] = useState(false);

  useEffect(() => {
    void load();
  }, [load]);

  function newSandbox() {
    if (!canCreate) return;
    void navigate({ to: "/sandboxes/new" });
  }

  return (
    <div className={styles.page}>
      {isMobile ? (
        <header className={styles.mobileHeader}>
          <div className={styles.headingRow}>
            <p className={styles.intro}>
              Isolated environments with their own API key and credit balance.
            </p>
            <InfoTooltip label="What is a sandbox?" layout="inline">
              {ABOUT}
            </InfoTooltip>
          </div>
          <Button
            disabled={!canCreate}
            fullWidth
            iconLeft={<Icon name="plus" size={18} />}
            onClick={newSandbox}
          >
            New sandbox
          </Button>
        </header>
      ) : (
        <header className={styles.header}>
          <div>
            <div className={styles.headingRow}>
              <h1 className={styles.title}>Sandboxes</h1>
              <InfoTooltip label="What is a sandbox?">{ABOUT}</InfoTooltip>
            </div>
            <p className={styles.intro}>
              Isolated environments with their own API key and credit balance.
            </p>
          </div>
          <Button
            disabled={!canCreate}
            iconLeft={<Icon name="plus" size={18} />}
            onClick={newSandbox}
          >
            New sandbox
          </Button>
        </header>
      )}

      {status === "idle" || (status === "loading" && !sandboxes) ? (
        <ListSkeleton />
      ) : (
        <>
      <Card className={styles.limits}>
        <button
          type="button"
          className={styles.limitsHead}
          aria-expanded={limitsOpen}
          onClick={() => setLimitsOpen((state) => !state)}
        >
          <span className={styles.limitsIcon}>
            <Icon name="info" size={16} />
          </span>
          <span className={styles.limitsText}>
            <span className={styles.quota}>
              {refreshing
                ? "Refreshing…"
                : `${active} of ${maxActive ?? "—"} sandboxes active`}
              {!refreshing && atQuota ? (
                <span className={styles.quotaHint}>
                  {" "}
                  — delete one to free up a slot
                </span>
              ) : null}
            </span>
            <span className={styles.disclosure}>
              {limitsOpen ? "Hide limits" : "View limits"}
            </span>
          </span>
          <Icon
            name="chevronDown"
            size={15}
            className={`${styles.chevron} ${limitsOpen ? styles.chevronOpen : ""}`}
          />
        </button>
        {limitsOpen ? (
          <div className={styles.limitsBody}>
            <div className={styles.limitStat}>
              <span className={styles.limitName}>Sandboxes in total</span>
              <span className={styles.limitValue}>{total}</span>
            </div>
            <div className={styles.limitStat}>
              <span className={styles.limitName}>Active right now</span>
              <span className={styles.limitValue}>
                {active} of {maxActive ?? "—"}
              </span>
            </div>
            <p className={styles.limitNote}>
              You can run up to {maxActive ?? "—"} active sandboxes at a time.
              {atQuota ? " Delete one to free up a slot." : null}
            </p>
          </div>
        ) : null}
      </Card>

      {status === "error" ? (
        <Alert
          tone="danger"
          title="Could not load your sandboxes"
          action={
            <Button size="sm" variant="secondary" onClick={() => void load()}>
              Retry
            </Button>
          }
        >
          The request to pf-developer-network failed.
        </Alert>
      ) : null}

      {status === "ready" && sandboxes?.length === 0 ? (
        <EmptyState
          title="No sandboxes yet"
          action={
            <Button disabled={!canCreate} onClick={newSandbox}>
              New sandbox
            </Button>
          }
        >
          A sandbox gives you a base URL, an API key scoped to the groups you
          choose, and a credit balance for generating test leads.
        </EmptyState>
      ) : null}

      <div className={styles.list}>
        {(sandboxes ?? []).map((sandbox) => (
          <SandboxCard key={sandbox.id} sandbox={sandbox} />
        ))}
      </div>
        </>
      )}
    </div>
  );
}
